import type {
  HealthcareService,
  Location,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import type * as appointments from '@seen/core/models/appointments/index'
import { Button } from '@seen/ui'
import { differenceInMinutes } from 'date-fns'
import { type JSX, useMemo, useState } from 'react'
import { AiRecommendedAppointmentSlots } from '#src/components/appointment/AiRecommendedAppointmentSlots.tsx'
import {
  EngineRecommendedSlots,
  EngineRecommendedSlotsSkeleton,
} from '#src/components/appointment/EngineRecommendedSlots.tsx'
import { RecommendedAppointmentSlots } from '#src/components/appointment/RecommendedAppointmentSlots.tsx'
import { slotRecommendationSearchFrom } from '#src/components/appointment/slotRecommendationSearchFrom.ts'
import { useEngineRecommendedSlots } from '#src/components/appointment/useEngineRecommendedSlots.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export interface RescheduleSlotRecommendationsProps {
  patient: Reference<Patient> | undefined
  practitioner: Reference<Practitioner> | undefined
  healthcareService: Reference<HealthcareService> | undefined
  location?: Reference<Location>
  start?: string
  end?: string
  // Dropped from conflict checks so the visit does not block its own slot.
  excludeAppointmentId: string
  // Visible-horizon start. Combined with `start`: the appointment's day is
  // always a floor, and this horizon moves the search later only when the
  // calendar-horizon flag is on.
  searchFrom?: string
  // False for cancelled, read-only, and practitioner-block appointments.
  canReschedule: boolean
  onSlotSelected: (slot: { start: string; end: string }) => void
}

export function RescheduleSlotRecommendations(
  props: RescheduleSlotRecommendationsProps
): JSX.Element | null {
  if (!props.canReschedule) {
    return null
  }
  return <RescheduleSlotRecommendationsList {...props} />
}

function RescheduleSlotRecommendationsList({
  patient,
  practitioner,
  healthcareService,
  location,
  start,
  end,
  excludeAppointmentId,
  searchFrom,
  onSlotSelected,
}: RescheduleSlotRecommendationsProps): JSX.Element | null {
  const aiFindNextSlotEnabled = useFeatureFlag(featureFlags.aiFindNextSlot)
  const schedulingEngineEnabled = useFeatureFlag(
    featureFlags.schedulingRulesEngine
  )
  const horizonEnabled = useFeatureFlag(
    featureFlags.slotRecommendationsFromCalendarHorizon
  )
  const [isFindingTime, setIsFindingTime] = useState(false)
  const durationMinutes = useMemo(() => {
    if (!start || !end) {
      return undefined
    }
    const minutes = differenceInMinutes(new Date(end), new Date(start))
    return minutes > 0 ? minutes : undefined
  }, [start, end])
  const slotSearchFrom = slotRecommendationSearchFrom({
    horizonEnabled,
    horizonStart: searchFrom,
    selectedStart: start,
  })
  const engineSlotsQuery = useEngineRecommendedSlots({
    patient,
    healthcareService,
    practitioner,
    location,
    durationMinutes,
    searchFrom: slotSearchFrom,
    excludeAppointmentId,
    enabled: schedulingEngineEnabled,
  })
  const engineSlotsActive =
    schedulingEngineEnabled && engineSlotsQuery.data?.hasConfig === true
  const engineSlotsPending =
    schedulingEngineEnabled &&
    engineSlotsQuery.data === undefined &&
    !engineSlotsQuery.isError

  if (!patient || !healthcareService || !practitioner) {
    return null
  }

  const selectSlot = (slot: appointments.AvailableAppointmentSlot): void => {
    if (!slot.period.start || !slot.period.end) {
      return
    }
    onSlotSelected({ start: slot.period.start, end: slot.period.end })
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Outlines stay on the new-booking grid. Clicking one there closes
          this appointment and opens a request, so reschedule applies the
          slot from the list instead. */}
      {engineSlotsActive && (
        <EngineRecommendedSlots
          selectedStart={start}
          selectedEnd={end}
          slots={engineSlotsQuery.data?.slots ?? []}
          rules={engineSlotsQuery.data?.rules ?? {}}
          isLoading={engineSlotsQuery.isLoading}
          onAppointmentSlotSelected={selectSlot}
        />
      )}
      {engineSlotsPending && <EngineRecommendedSlotsSkeleton />}
      {!engineSlotsActive && !engineSlotsPending && aiFindNextSlotEnabled && (
        <AiRecommendedAppointmentSlots
          selectedStart={start}
          selectedEnd={end}
          searchFrom={slotSearchFrom}
          patient={patient}
          healthcareService={healthcareService}
          practitioner={practitioner}
          excludeAppointmentId={excludeAppointmentId}
          onAppointmentSlotSelected={selectSlot}
        />
      )}
      {!engineSlotsActive && !engineSlotsPending && !aiFindNextSlotEnabled && (
        <Button
          disabled={isFindingTime}
          className="w-fit p-0 text-sm"
          variant="link"
          onClick={() => setIsFindingTime(true)}
        >
          Find available time
        </Button>
      )}
      {!engineSlotsActive &&
        !engineSlotsPending &&
        !aiFindNextSlotEnabled &&
        isFindingTime && (
          <RecommendedAppointmentSlots
            selectedStart={start}
            selectedEnd={end}
            patient={patient}
            healthcareService={healthcareService}
            practitioner={practitioner}
            excludeAppointmentId={excludeAppointmentId}
            onAppointmentSlotSelected={selectSlot}
          />
        )}
    </div>
  )
}
